import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CambiosItemSubido, EstadoSubirTodo, TipoDocumento } from '@alexiarest/shared';
import { api } from '../../api/client';

/*
 * Datos del onboarding «Súbelo todo». Mientras haya archivos en cola o
 * leyéndose se pregunta cada 2 s: la lectura va en segundo plano (cola + worker)
 * y la pantalla tiene que ver avanzar cada fila sin recargar.
 */

export const CLAVE_SUBIR_TODO = ['subir-todo'] as const;

const leyendo = (estado: EstadoSubirTodo | { job: null } | undefined) =>
  Boolean(estado?.job && 'archivos' in estado && estado.archivos.some((a) => a.estado === 'en_cola' || a.estado === 'leyendo'));

export function useSubirTodo() {
  return useQuery({
    queryKey: CLAVE_SUBIR_TODO,
    queryFn: () => api.getSubirTodo(),
    refetchInterval: (query) => (leyendo(query.state.data) ? 2000 : false),
  });
}

/** Al guardar, lo guardado aparece en Gastos, Carta y Resultados: se refresca todo. */
function useRefrescar() {
  const qc = useQueryClient();
  return (estado?: EstadoSubirTodo) => {
    if (estado) qc.setQueryData(CLAVE_SUBIR_TODO, estado);
    void qc.invalidateQueries({ queryKey: CLAVE_SUBIR_TODO });
  };
}

export function useEmpezarSubirTodo() {
  const refrescar = useRefrescar();
  return useMutation({ mutationFn: () => api.empezarSubirTodo(), onSuccess: (estado) => refrescar(estado) });
}

export function useSubirArchivo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (input: { jobId: string; file: Blob; nombre: string; locationId?: string }) =>
      api.subirArchivoSubirTodo(input.jobId, input.file, input.nombre, input.locationId ? { locationId: input.locationId } : {}),
    onSettled: () => refrescar(),
  });
}

export function useDecirTipo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (input: { jobId: string; fileId: string; tipo: TipoDocumento }) => api.decirTipoSubirTodo(input.jobId, input.fileId, input.tipo),
    onSettled: () => refrescar(),
  });
}

export function useReintentar() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (input: { jobId: string; fileId: string }) => api.reintentarSubirTodo(input.jobId, input.fileId),
    onSettled: () => refrescar(),
  });
}

export function useEditarItem() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (input: { jobId: string; itemId: string; cambios: CambiosItemSubido }) => api.editarItemSubirTodo(input.jobId, input.itemId, input.cambios),
    onSettled: () => refrescar(),
  });
}

export function useGuardarGrupo() {
  const qc = useQueryClient();
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (input: { jobId: string; grupo: 'gastos' | 'carta'; itemIds?: string[] }) => api.guardarSubirTodo(input.jobId, input.grupo, input.itemIds),
    onSuccess: (resultado) => {
      refrescar(resultado.estado);
      // Gastos, carta, panel y resultados cambian: que se vuelvan a pedir.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== CLAVE_SUBIR_TODO[0] });
    },
  });
}
